import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Button, Group, Menu, Text } from "@mantine/core"
import { useEffect, useState } from "react"
import { browserBannerStore, dismissModeBanner, isModeBannerDismissed } from "./dismiss"
import type { IProps } from "./interfaces"

/**
 * Shown when the meeting's recording mode differs from the user's default for
 * new meetings. Never converts this meeting — the CTA only changes the default.
 * Dismissing it hides this mode pair in this browser, so older meetings don't
 * repeat the note.
 */
export const MeetingRecordingMismatchBanner = ({
  recordedAs,
  defaultForNew,
  onChangeDefault,
  changing = false,
}: IProps): React.JSX.Element | null => {
  const { i18n } = useLingui()
  const [hidden, setHidden] = useState((): boolean =>
    isModeBannerDismissed(recordedAs, defaultForNew, browserBannerStore()),
  )

  useEffect(() => {
    setHidden(isModeBannerDismissed(recordedAs, defaultForNew, browserBannerStore()))
  }, [recordedAs, defaultForNew])

  if (recordedAs === defaultForNew || hidden) return null

  const dismiss = (): void => {
    dismissModeBanner(recordedAs, defaultForNew, browserBannerStore())
    setHidden(true)
  }

  return (
    <Alert
      color="info"
      variant="light"
      mb="md"
      data-testid="recording-mode-mismatch"
      withCloseButton
      closeButtonLabel={i18n._(msg`Dismiss`)}
      onClose={dismiss}
    >
      <Group justify="space-between" gap="sm" wrap="wrap">
        <Text size="sm">
          {recordedAs === "full" ? (
            <Trans>
              This meeting was recorded in <strong>Full</strong>. Your default for new meetings is <strong>Lite</strong>
              .
            </Trans>
          ) : (
            <Trans>
              This meeting was recorded in <strong>Lite</strong>. Your default for new meetings is <strong>Full</strong>
              .
            </Trans>
          )}
        </Text>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <Button size="xs" variant="light" loading={changing} style={{ flexShrink: 0 }}>
              <Trans>Change default for next time</Trans>
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>
              <Trans>Default for new meetings</Trans>
            </Menu.Label>
            <Menu.Item onClick={() => onChangeDefault("full")} disabled={defaultForNew === "full"}>
              <Trans>Full</Trans>
            </Menu.Item>
            <Menu.Item onClick={() => onChangeDefault("lite")} disabled={defaultForNew === "lite"}>
              <Trans>Lite</Trans>
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Alert>
  )
}
